import { useEffect, useState, useMemo } from 'react';
import { useParams, useHistory } from 'quartz-routing/react-router';
import { View } from 'foundations-components/transitional/components/View';
import { usageTracker } from '../utils/usageTracker';
import { useDynamicProjectFetchPortalMethodQuery, useDynamicProjectsFetchPortalMethodQuery, useAuthenticatedPortalMethodQuery } from '../../__generated__/chirp/com/hubspot/portals/platform/reference/be/rpc/PortalRpc.hooks';
import { usePortalsAuth } from 'portals-platform-lib';
import { Text } from 'foundations-components/components/Text';
import { Flex } from 'foundations-components/components/Flex';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { CardWrapper, CardHeader, CardSection } from 'foundations-components/transitional/components/Card';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export function getPortalUrl() {
  var _domainOverrideTag$ge;
  const prefixTag = document.querySelector('meta[name="x-hs-portals-prefix"]');
  const prefix = prefixTag === null || prefixTag === void 0 ? void 0 : prefixTag.getAttribute('content');
  const domainOverrideTag = document.querySelector('meta[name="x-hs-portals-domain-override"]');
  const origin = ((_domainOverrideTag$ge = domainOverrideTag === null || domainOverrideTag === void 0 ? void 0 : domainOverrideTag.getAttribute('content')) !== null && _domainOverrideTag$ge !== void 0 ? _domainOverrideTag$ge : window.location.origin).replace(/\/$/, '');
  return prefix ? `${origin}${prefix}` : undefined;
}
export function ProjectPOC() {
  var _projectsError$networ, _projectError$network;
  const {
    id
  } = useParams();
  const history = useHistory();
  const {
    data: __authenticatedPortalMethodData
  } = useAuthenticatedPortalMethodQuery({
    variables: {
      message: 'from-client'
    }
  });
  const {
    user
  } = usePortalsAuth();
  const contactId = user === null || user === void 0 ? void 0 : user.contactVid;
  useEffect(() => {
    usageTracker.track('portals-platform-reference-fe', 'page viewed', {
      screen: 'generated-app-route',
      subscreen: 'project-poc'
    });
  }, []);
  const {
    data: projectsData,
    error: projectsError
  } = useDynamicProjectsFetchPortalMethodQuery({
    variables: {
      accountId: contactId,
      message: 'Hello World'
    }
  });
  const projectsAuthError = projectsError === null || projectsError === void 0 || (_projectsError$networ = projectsError.networkError) === null || _projectsError$networ === void 0 || (_projectsError$networ = _projectsError$networ.cause) === null || _projectsError$networ === void 0 ? void 0 : _projectsError$networ.cause;
  const projects = useMemo(() => (projectsData === null || projectsData === void 0 ? void 0 : projectsData.projects) || [], [projectsData === null || projectsData === void 0 ? void 0 : projectsData.projects]);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  useEffect(() => {
    if (id) {
      const projectId = parseInt(id, 10);
      if (!isNaN(projectId)) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setSelectedProjectId(projectId);
      }
    } else if (projects.length > 0 && selectedProjectId === null) {
      const firstProjectId = projects[0].projectId;
      setSelectedProjectId(firstProjectId);
      history.replace(`/projects/${firstProjectId}`);
    }
  }, [id, projects, selectedProjectId, history]);
  const {
    data: projectData,
    error: projectError
  } = useDynamicProjectFetchPortalMethodQuery({
    variables: {
      accountId: contactId,
      objectId: selectedProjectId !== null && selectedProjectId !== void 0 ? selectedProjectId : 0,
      message: 'Hello World'
    },
    skip: selectedProjectId === null
  });
  const projectAccessError = projectError === null || projectError === void 0 || (_projectError$network = projectError.networkError) === null || _projectError$network === void 0 || (_projectError$network = _projectError$network.cause) === null || _projectError$network === void 0 ? void 0 : _projectError$network.cause;
  const handleProjectClick = projectId => {
    setSelectedProjectId(projectId);
    history.push(`/projects/${projectId}`);
  };
  const sidebarContent = /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    gap: "300",
    children: [/*#__PURE__*/_jsx(View, {
      paddingBlock: "400",
      paddingInline: "400",
      children: /*#__PURE__*/_jsx(Text, {
        typography: "heading-300",
        children: "Projects"
      })
    }), projects.length === 0 && !projectsAuthError && /*#__PURE__*/_jsx(View, {
      paddingBlock: "300",
      paddingInline: "400",
      children: /*#__PURE__*/_jsx(Text, {
        typography: "body-100",
        color: "text-core-subtle",
        children: "No projects available"
      })
    }), projects.map(project => /*#__PURE__*/_jsx(View, {
      paddingBlock: "300",
      paddingInline: "400",
      backgroundColor: selectedProjectId === project.projectId ? 'fill-secondary-default' : undefined,
      cursor: "pointer",
      onClick: () => handleProjectClick(project.projectId),
      children: /*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        gap: "100",
        children: [/*#__PURE__*/_jsx(Text, {
          typography: "body-200",
          as: "strong",
          children: project.projectName
        }), /*#__PURE__*/_jsx(Text, {
          typography: "body-100",
          color: "text-core-subtle",
          children: project.projectDescription
        })]
      })
    }, project.projectId))]
  });
  const mainContent = /*#__PURE__*/_jsxs(View, {
    children: [projectsAuthError && /*#__PURE__*/_jsx(Alert, {
      type: "danger",
      marginBlockEnd: "500",
      children: /*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        gap: "200",
        children: [/*#__PURE__*/_jsx(Text, {
          typography: "body-200",
          as: "strong",
          children: "Unable to load projects"
        }), /*#__PURE__*/_jsx(Text, {
          typography: "body-200",
          children: projectsAuthError.denialReason || projectsAuthError.chirpErrorMessage || 'An error occurred while loading the projects list. Please try again.'
        })]
      })
    }), projectAccessError && selectedProjectId && /*#__PURE__*/_jsx(Alert, {
      type: "danger",
      marginBlockEnd: "500",
      children: /*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        gap: "200",
        children: [/*#__PURE__*/_jsx(Text, {
          typography: "body-200",
          as: "strong",
          children: "Unable to load project"
        }), /*#__PURE__*/_jsx(Text, {
          typography: "body-200",
          children: projectAccessError.resourceType === 'PROJECT' ? `You do not have access to project ${projectAccessError.resourceId}.` : projectAccessError.chirpErrorMessage || 'An error occurred while loading the project details.'
        }), projectAccessError.contactId && /*#__PURE__*/_jsxs(Text, {
          typography: "body-100",
          color: "text-core-subtle",
          children: ["Contact ID: ", projectAccessError.contactId]
        })]
      })
    }), projectData && !projectAccessError && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs(CardWrapper, {
        marginBlockEnd: "500",
        children: [/*#__PURE__*/_jsx(CardHeader, {
          title: "Project Information"
        }), /*#__PURE__*/_jsx(CardSection, {
          children: /*#__PURE__*/_jsxs(Flex, {
            flexDirection: "column",
            gap: "300",
            children: [/*#__PURE__*/_jsxs(Flex, {
              gap: "400",
              children: [/*#__PURE__*/_jsx(Text, {
                typography: "body-200",
                as: "strong",
                children: "Project Name:"
              }), /*#__PURE__*/_jsx(Text, {
                typography: "body-200",
                children: projectData.projectName
              })]
            }), /*#__PURE__*/_jsxs(Flex, {
              gap: "400",
              children: [/*#__PURE__*/_jsx(Text, {
                typography: "body-200",
                as: "strong",
                children: "Project ID:"
              }), /*#__PURE__*/_jsx(Text, {
                typography: "body-200",
                children: selectedProjectId
              })]
            }), /*#__PURE__*/_jsxs(Flex, {
              gap: "400",
              children: [/*#__PURE__*/_jsx(Text, {
                typography: "body-200",
                as: "strong",
                children: "Description:"
              }), /*#__PURE__*/_jsx(Text, {
                typography: "body-200",
                children: projectData.projectDescription
              })]
            }), projectData.status && /*#__PURE__*/_jsxs(Flex, {
              gap: "400",
              children: [/*#__PURE__*/_jsx(Text, {
                typography: "body-200",
                as: "strong",
                children: "Status:"
              }), /*#__PURE__*/_jsx(Text, {
                typography: "body-200",
                children: projectData.status
              })]
            }), projectData.planningStage && /*#__PURE__*/_jsxs(Flex, {
              gap: "400",
              children: [/*#__PURE__*/_jsx(Text, {
                typography: "body-200",
                as: "strong",
                children: "Planning Stage:"
              }), /*#__PURE__*/_jsx(Text, {
                typography: "body-200",
                children: projectData.planningStage
              })]
            }), projectData.targetDueDate && /*#__PURE__*/_jsxs(Flex, {
              gap: "400",
              children: [/*#__PURE__*/_jsx(Text, {
                typography: "body-200",
                as: "strong",
                children: "Target Due Date:"
              }), /*#__PURE__*/_jsx(Text, {
                typography: "body-200",
                children: new Date(projectData.targetDueDate).toLocaleDateString()
              })]
            })]
          })
        })]
      }), projectData.tasks && projectData.tasks.length > 0 && /*#__PURE__*/_jsxs(CardWrapper, {
        marginBlockEnd: "500",
        children: [/*#__PURE__*/_jsx(CardHeader, {
          title: "Tasks"
        }), /*#__PURE__*/_jsx(CardSection, {
          children: /*#__PURE__*/_jsx(Flex, {
            flexDirection: "column",
            gap: "400",
            children: projectData.tasks.map(task => /*#__PURE__*/_jsx(View, {
              paddingBlock: "300",
              paddingInline: "300",
              backgroundColor: "fill-secondary-default",
              borderRadius: "300",
              children: /*#__PURE__*/_jsxs(Flex, {
                flexDirection: "column",
                gap: "200",
                children: [/*#__PURE__*/_jsxs(Flex, {
                  justifyContent: "space-between",
                  alignItems: "center",
                  children: [/*#__PURE__*/_jsx(Text, {
                    typography: "body-200",
                    as: "strong",
                    children: task.title
                  }), task.priority && /*#__PURE__*/_jsxs(Text, {
                    typography: "body-100",
                    color: "text-core-subtle",
                    children: ["Priority: ", task.priority]
                  })]
                }), /*#__PURE__*/_jsxs(Flex, {
                  gap: "400",
                  children: [/*#__PURE__*/_jsxs(Text, {
                    typography: "body-100",
                    color: "text-core-subtle",
                    children: ["Task ID: ", task.taskId]
                  }), task.dueDate && /*#__PURE__*/_jsxs(Text, {
                    typography: "body-100",
                    color: "text-core-subtle",
                    children: ["Due: ", new Date(task.dueDate).toLocaleDateString()]
                  }), task.assigneeId && /*#__PURE__*/_jsxs(Text, {
                    typography: "body-100",
                    color: "text-core-subtle",
                    children: ["Assignee ID: ", task.assigneeId]
                  })]
                })]
              })
            }, task.taskId))
          })
        })]
      })]
    })]
  });
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "row",
    children: [/*#__PURE__*/_jsx(View, {
      minInlineSize: "250px",
      maxInlineSize: "250px",
      paddingBlock: "400",
      borderInlineEndStyle: "solid",
      borderInlineEndWidth: "100",
      borderInlineEndColor: "border-core-default",
      children: sidebarContent
    }), /*#__PURE__*/_jsx(View, {
      flex: "1",
      padding: "600",
      children: mainContent
    })]
  });
}